export interface ProductSkuLabelInput {
  skuCode: string;
  productName: string;
  variantName?: string | null;
  classificationVariant?: string | null;
  offerLabel?: string | null;
  packSize?: number | null;
  boxesPerSale?: number | null;
}

export interface ProductSkuLabel {
  description: string;
  details: string;
  code: string;
}

/** Decode display text only; source identifiers and filter values stay exact. */
export function readableSourceLabel(value: string): string {
  const entities: Record<string, string> = { "&amp;": "&", "&quot;": '"', "&#39;": "'", "&apos;": "'", "&lt;": "<", "&gt;": ">", "&nbsp;": " " };
  return value.replace(/&(?:amp|quot|apos|lt|gt|nbsp);|&#39;/g, (entity) => entities[entity] ?? entity);
}

function usefulVariant(value: string | null | undefined, description: string): string | null {
  const variant = value?.trim();
  if (!variant || variant.toLowerCase() === "default") return null;
  return description.toLowerCase().includes(variant.toLowerCase()) ? null : variant;
}

function countLabel(value: number, noun: string): string {
  return `${value.toLocaleString()} ${noun}${value === 1 ? "" : "s"}`;
}

/** Human meaning first; the source SKU code stays visible as the exact key. */
export function productSkuLabel(input: ProductSkuLabelInput): ProductSkuLabel {
  const description = readableSourceLabel(input.productName)
    .trim()
    .replace(/^\(?\d+\)?\s*[.)-]\s*/, "")
    || input.skuCode;
  const variant = usefulVariant(input.classificationVariant ?? input.variantName, description);
  const details: string[] = [];
  if (variant) details.push(variant);
  if (typeof input.packSize === "number" && input.packSize > 0) {
    details.push(`${input.packSize.toLocaleString()}-pack`);
  }
  const offer = input.offerLabel?.trim();
  if (offer) details.push(offer);
  if (
    typeof input.boxesPerSale === "number"
    && input.boxesPerSale > 0
    && (input.packSize === null || input.packSize === undefined || input.boxesPerSale !== input.packSize)
  ) {
    details.push(`${countLabel(input.boxesPerSale, "unit")}/sale`);
  }
  return {
    description,
    details: details.join(" · "),
    code: `SKU ${input.skuCode}`,
  };
}
